import type React from 'react';
import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { PyMuiIconButton, PyMuiMenu, PyMuiMenuItem } from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents';
import FormatAlignLeftOutlinedIcon from '@mui/icons-material/FormatAlignLeftOutlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';

type Props = {
  editor: Editor;
};

const ALIGNMENT_OPTIONS = [
  { alignment: 'left', Icon: FormatAlignLeftIcon },
  { alignment: 'center', Icon: FormatAlignCenterIcon },
  { alignment: 'right', Icon: FormatAlignRightIcon },
  { alignment: 'justify', Icon: FormatAlignJustifyIcon },
] as const;

type AlignmentOption = (typeof ALIGNMENT_OPTIONS)[number]['alignment'];

const isAlignmentActive = (editor: Editor) =>
  editor.isActive({ textAlign: 'left' }) ||
  editor.isActive({ textAlign: 'center' }) ||
  editor.isActive({ textAlign: 'right' }) ||
  editor.isActive({ textAlign: 'justify' });

const AlignmentMenu: React.FC<Props> = ({ editor }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [active, setActive] = useState<boolean>(() => isAlignmentActive(editor));

  useEffect(() => {
    const updateActive = () => {
      setActive(isAlignmentActive(editor));
    };

    updateActive();
    editor.on('transaction', updateActive);
    editor.on('selectionUpdate', updateActive);

    return () => {
      editor.off('transaction', updateActive);
      editor.off('selectionUpdate', updateActive);
    };
  }, [editor]);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const handleAlignmentClick = (alignment: AlignmentOption) => {
    editor.chain().focus().setTextAlign(alignment).run();
    closeMenu();
  };

  return (
    <>
      <PyMuiIconButton attributeName="textAlign" onClick={openMenu} isActive={active}>
        <FormatAlignLeftOutlinedIcon />
      </PyMuiIconButton>
      <PyMuiMenu
        testId="alignment-menu"
        id="select-alignment-menu"
        menuListProps="select-alignment-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={closeMenu}
        direction="row"
      >
        {ALIGNMENT_OPTIONS.map(({ alignment, Icon }) => (
          <PyMuiMenuItem
            key={alignment}
            testId={`align-${alignment}`}
            onClick={() => handleAlignmentClick(alignment)}
            menuItem={<Icon />}
          />
        ))}
      </PyMuiMenu>
    </>
  );
};

export default AlignmentMenu;
